import BaseController from "./BaseController";
import Formatter from "../model/formatter";
import JSONModel from "sap/ui/model/json/JSONModel";
import BindingMode from "sap/ui/model/BindingMode";
import Control from "sap/ui/core/Control";
import Table from "sap/ui/table/Table";
import Column from "sap/ui/table/Column";
import Input from "sap/m/Input";
import type { InputBase$ChangeEvent } from "sap/m/InputBase";
import Text from "sap/m/Text";
import Label from "sap/m/Label";
import MessageToast from "sap/m/MessageToast";
import MessageBox from "sap/m/MessageBox";
import { InputType } from "sap/m/library";
import { HorizontalAlign, TextAlign } from "sap/ui/core/library";
import type { Select$ChangeEvent } from "sap/m/Select";
import type ResourceBundle from "sap/base/i18n/ResourceBundle";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

/** One matrix cell — a persisted product×location price. `null` in a row means "no price set". */
interface PriceCell {
	amount: number;
	currencyCode: string;
	priceId: string;
}

/** One matrix row — a product plus its price cell per location id. */
interface PriceRow {
	productId: string;
	productCode: string;
	productName: string;
	productFamilyId: string;
	cells: Record<string, PriceCell | null>;
}

/** Active location = one matrix column. */
interface LocationColumn {
	id: string;
	code: string;
	name: string;
	currencyCode: string;
}

/** view-model shape driving the filter bar. `familyFilter`/`locationFilter` are ids or "ALL". */
interface FilterState {
	familyFilter: string;
	locationFilter: string;
	families: { id: string; name: string }[];
	locations: { id: string; name: string }[];
}

// --- Minimal shapes of the read-only OData rows we consume (flat DTOs, PascalCase). ---
interface ODataFeed<T> { value: T[]; }
interface ODataLocation { Id: string; Code: string; Name: string; CurrencyCode: string; }
interface ODataProduct { Id: string; Code: string; Name: string; ProductFamilyId: string; }
interface ODataPrice { Id: string; ProductId: string; LocationId: string; Amount: number; CurrencyCode: string; }
interface ODataFamily { Id: string; Name: string; }

const ALL = "ALL";

/**
 * Products × locations price matrix (Task 9). The OData feed `/odata/v4/ProductPrices` is a flat,
 * read-only list; we pivot it in the controller into `matrix>/rows` (one row per visible product,
 * one cell per active location) and build the location columns dynamically — the set of columns is
 * data-driven, so it cannot be declared in XML.
 *
 * Editing never touches the read-only feed (that caused an auto-PATCH bug elsewhere — see the
 * IsHidden lesson in the sapui5-b2b skill): the editable cells are `sap.m.Input`s bound OneWay to
 * a local JSONModel, and persistence goes through the REST `PATCH`/`DELETE
 * /api/v1/products/{id}/prices/{locationId}` endpoints, after which we reload (which also rolls a
 * failed edit back to the server value). Write columns are only rendered when the caller holds
 * `pricing.write`; otherwise cells are plain `sap.m.Text`.
 *
 * @namespace computec.b2b.portal.controller
 */
export default class Pricing extends BaseController {
	private locations: LocationColumn[] = [];
	private allRows: PriceRow[] = [];
	private bundle?: ResourceBundle;

	public onInit(): void {
		const initial: FilterState = { familyFilter: ALL, locationFilter: ALL, families: [], locations: [] };
		this.getView().setModel(new JSONModel({ rows: [] }), "matrix");
		this.getView().setModel(new JSONModel(initial), "view");
		this.getRouter().getRoute("pricing")?.attachPatternMatched(() => { void this.onShow(); });
	}

	private async onShow(): Promise<void> {
		this.bundle = await this.getResourceBundle();
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			// 1. Active locations → columns, ordered by SortOrder.
			const locs = await fetcher.json<ODataFeed<ODataLocation>>(
				`/odata/v4/Locations?$filter=${encodeURIComponent("Status eq 'Active'")}&$orderby=SortOrder`);
			this.locations = (locs?.value ?? []).map((l) => ({
				id: l.Id, code: l.Code, name: l.Name, currencyCode: l.CurrencyCode
			}));
			// 2. Visible products → rows (feed already scopes hidden/inactive out for non-CompuTec).
			const prods = await fetcher.json<ODataFeed<ODataProduct>>(
				"/odata/v4/Products?$orderby=Code&$select=Id,Code,Name,ProductFamilyId");
			// 3. Visible prices (flat) → pivot into cells.
			const prices = await fetcher.json<ODataFeed<ODataPrice>>("/odata/v4/ProductPrices");
			this.allRows = this.pivot(prods?.value ?? [], prices?.value ?? []);
			// 4. Product families → family filter (non-fatal: a families failure must not break the matrix).
			let families: ODataFamily[] = [];
			try {
				const fam = await fetcher.json<ODataFeed<ODataFamily>>(
					"/odata/v4/ProductFamilies?$orderby=Name&$select=Id,Name");
				families = fam?.value ?? [];
			} catch {
				families = [];
			}
			this.populateFilters(families);
			this.applyView();
		} catch (err) {
			MessageBox.error(problemMessage((err as ResponseError).bodyText, this.i18nText("pricing.load.failure")));
		}
	}

	/**
	 * Flat prices → PriceRow[]: rows = ALL visible products (even those with no price yet); each row
	 * carries a cell per active location — the persisted price, or `null` when none exists.
	 */
	private pivot(products: ODataProduct[], prices: ODataPrice[]): PriceRow[] {
		const byProduct = new Map<string, Record<string, PriceCell>>();
		for (const pr of prices) {
			let owned = byProduct.get(pr.ProductId);
			if (!owned) {
				owned = {};
				byProduct.set(pr.ProductId, owned);
			}
			owned[pr.LocationId] = { amount: pr.Amount, currencyCode: pr.CurrencyCode, priceId: pr.Id };
		}
		return products.map((p) => {
			const owned = byProduct.get(p.Id) ?? {};
			const cells: Record<string, PriceCell | null> = {};
			for (const loc of this.locations) {
				cells[loc.id] = owned[loc.id] ?? null;
			}
			return {
				productId: p.Id,
				productCode: p.Code,
				productName: p.Name,
				productFamilyId: p.ProductFamilyId,
				cells
			};
		});
	}

	/** Fills the family/location filter dropdowns (each prefixed with an "ALL" entry). */
	private populateFilters(families: ODataFamily[]): void {
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/families", [
			{ id: ALL, name: this.i18nText("pricing.filter.family.all") },
			...families.map((f) => ({ id: f.Id, name: f.Name }))
		]);
		view.setProperty("/locations", [
			{ id: ALL, name: this.i18nText("pricing.filter.location.all") },
			...this.locations.map((l) => ({ id: l.id, name: `${l.code} (${l.currencyCode})` }))
		]);
	}

	/** Applies the family (rows) + location (columns) filters against the loaded data. */
	private applyView(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as FilterState;
		const rows = state.familyFilter === ALL
			? this.allRows
			: this.allRows.filter((r) => r.productFamilyId === state.familyFilter);
		(this.getView().getModel("matrix") as JSONModel).setProperty("/rows", rows);

		const columns = state.locationFilter === ALL
			? this.locations
			: this.locations.filter((l) => l.id === state.locationFilter);
		this.buildColumns(columns);
	}

	/** Rebuilds the table columns: a fixed product column + one column per (filtered) location. */
	private buildColumns(columns: LocationColumn[]): void {
		const table = this.byId("priceTable") as Table;
		// destroy (not just remove) — onShow rebuilds columns on every reload/edit, so detaching
		// without disposing would leak the old Column + cell-template controls each time.
		table.destroyColumns();
		table.addColumn(new Column({
			label: new Label({ text: this.i18nText("pricing.col.product") }),
			template: new Text({ text: { path: "matrix>productName" } }),
			width: "20rem"
		}));

		// Columns are built here, one per location, so no view XML exists to bind editability in.
		// This is the named boundary of spec 22 S-06: a logic check, not a model read.
		const canWrite = this.getOwnerComponent().getPermissions().has("pricing.write");

		for (const loc of columns) {
			const currency = loc.currencyCode;
			const cellPath = `matrix>cells/${loc.id}/amount`;
			const template: Control = canWrite
				? new Input({
					// OneWay: the Input never writes back into the JSONModel — persistence is REST,
					// and reload (onShow) refreshes the value; this also avoids a null-parent write.
					value: { path: cellPath, mode: BindingMode.OneWay },
					type: InputType.Number,
					textAlign: TextAlign.End,
					placeholder: this.i18nText("pricing.edit.placeholder"),
					// change (not submit): submit fires only on Enter, so tabbing/clicking away would
					// silently lose the edit on the next reload — change fires on both blur and Enter.
					change: (e: InputBase$ChangeEvent) => { void this.onCellSubmit(e, loc.id); }
				})
				: new Text({
					text: { path: cellPath, formatter: (amount: number | null | undefined) => this.fmtCell(amount, currency) }
				});
			table.addColumn(new Column({
				label: new Label({ text: `${loc.code} (${loc.currencyCode})` }),
				template,
				width: "10rem",
				hAlign: HorizontalAlign.End
			}));
		}
	}

	private async onCellSubmit(event: InputBase$ChangeEvent, locationId: string): Promise<void> {
		const input = event.getSource();
		const ctx = input.getBindingContext("matrix");
		if (!ctx) {
			return;
		}
		const productId = ctx.getProperty("productId") as string;
		const raw = input.getValue();
		const n = Number(raw);
		const fetcher = this.getOwnerComponent().getAuthFetch();
		const url = `/api/v1/products/${productId}/prices/${locationId}`;
		try {
			if (raw === "" || !Number.isFinite(n) || n <= 0) {
				// Empty / non-numeric / non-positive → remove the price. `.json()` throws HttpError on non-2xx.
				await fetcher.json(url, { method: "DELETE" });
				MessageToast.show(this.i18nText("pricing.remove.success"));
			} else {
				await fetcher.json(url, { method: "PATCH", body: JSON.stringify({ amount: n }) });
				MessageToast.show(this.i18nText("pricing.save.success"));
			}
			await this.onShow();
		} catch (err) {
			MessageBox.error(problemMessage((err as ResponseError).bodyText, this.i18nText("pricing.load.failure")));
			await this.onShow(); // reload → rollback the edited cell to the server value
		}
	}

	public onFamilySelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? ALL;
		(this.getView().getModel("view") as JSONModel).setProperty("/familyFilter", key);
		this.applyView();
	}

	public onLocationSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? ALL;
		(this.getView().getModel("view") as JSONModel).setProperty("/locationFilter", key);
		this.applyView();
	}

	public onRefresh(): void {
		void this.onShow();
	}

	/** Read-only cell rendering: money in the column's currency, or the "no price" dash. */
	private fmtCell(amount: number | null | undefined, currencyCode: string): string {
		if (amount === null || amount === undefined) {
			return this.i18nText("pricing.noPrice");
		}
		return Formatter.formatMoney(amount, currencyCode);
	}

	/** Synchronous i18n lookup (bundle cached at the start of onShow); falls back to the key. */
	private i18nText(key: string): string {
		return this.bundle?.getText(key) ?? key;
	}

	/** ProblemDetails-aware error formatter — same shape the REST endpoints return (see Products.controller.ts). */
}
